<template>
  <div id="partner-form" class="partner-form-section">
    <h2>Apply for Partnership</h2>
    <p class="form-description">
      Fill out the form below to join our network of professional partners
    </p>

    <div
      class="form-container"
      data-aos="fade-up"
      data-aos-duration="800"
      data-aos-once="true"
    >
      <form @submit.prevent="submitPartnerForm" v-if="!formSubmitted">
        <div class="form-group">
          <label for="name">Name</label>
          <input
            type="text"
            id="name"
            v-model="partnerForm.name"
            placeholder="Your full name"
            required
          />
        </div>

        <div class="form-group">
          <label for="email">Email</label>
          <input
            type="email"
            id="email"
            v-model="partnerForm.email"
            placeholder="Your email address"
            required
          />
        </div>

        <div class="form-row">
          <div class="form-group country-code">
            <label for="countryCode">Country Code</label>
            <select
              id="countryCode"
              v-model="partnerForm.countryCode"
              required
              class="country-code-select"
            >
              <option value="+91">+91 (India)</option>
              <option value="+1">+1 (United States/Canada)</option>
              <option value="+44">+44 (United Kingdom)</option>
              <option value="+61">+61 (Australia)</option>
              <option value="+971">+971 (United Arab Emirates)</option>
              <!-- Add more options as needed -->
            </select>
          </div>

          <div class="form-group mobile-number">
            <label for="mobile">Mobile Number</label>
            <input
              type="tel"
              id="mobile"
              v-model="partnerForm.mobile"
              placeholder="Your mobile number"
              required
            />
            <p v-if="message" class="form-message">{{ message }}</p>
          </div>
        </div>

        <div class="form-btn-group">
          <button type="submit" class="submit-button">
            Submit Application <RiArrowRightLine />
          </button>
        </div>
      </form>
      <SuccessMsg bg="rgb(0,0,0)" v-else />
    </div>
  </div>
</template>

<script setup>
import { RiArrowRightLine } from "@remixicon/vue";
import axios from "axios";
import SuccessMsg from "~/components/ui/SuccessMsg.vue";

const { $Aos } = useNuxtApp();
const config = useRuntimeConfig();
// Form data
const partnerForm = ref({
  name: "",
  email: "",
  countryCode: "+1",
  mobile: "",
});
const message = ref("");
const formSubmitted = ref(false);

const submitPartnerForm = async () => {
  try {
    const { name, email, countryCode, mobile } = partnerForm.value;
    if (name === "" || email === "") return;
    const mobileRegix = /^\+(?:[0-9] ?){6,14}[0-9]$/;
    let mobilenumber = `${countryCode}${mobile}`;
    if (!mobileRegix.test(mobilenumber)) {
      message.value = "Please enter a valid mobile number";
      return;
    }
    let response = await axios.post(`${config.public.NUXT_API_URL}/contact`, {
      name,
      email,
      mobile: mobilenumber,
      message: "Contact from Travvir Partnership Form",
    });
    if (response.status === 200) {
      partnerForm.value = {
        name: "",
        email: "",
        countryCode: "+91",
        mobile: "",
      };
      formSubmitted.value = true;
      message.value =
        "Thank you for your interest! We will contact you shortly.";
    } else {
      console.error("Error submitting form:", response);
    }
  } catch (error) {
    console.error("Error submitting form:", error);
  }
};

onMounted(() => {
  $Aos.init();
});
</script>

<style lang="scss" scoped>
.partner-form-section {
  padding: 6rem 2rem;

  @include respond-below(sm) {
    padding: 4rem 1rem;
  }

  h2 {
    font-style: normal;
    font-weight: 600;
    font-size: 39px;
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.9);
    font-family: inherit;
    text-align: center;
    margin-bottom: 2rem;

    @include respond-below(sm) {
      font-size: 28px;
    }
  }

  .form-description {
    text-align: center;
    max-width: 600px;
    margin: 0 auto 3rem;
    font-size: 18px;
    color: rgba(255, 255, 255, 0.4);
  }

  .form-container {
    max-width: 800px;
    margin: 0 auto;
    background: rgba(255, 255, 255, 0.05);
    padding: 2rem;
    border-radius: 12px;
    box-shadow: 0 5px 30px rgba(0, 0, 0, 0.15);

    form {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;

      .form-group {
        display: flex;
        flex-direction: column;

        label {
          font-weight: 500;
          font-size: 16px;
          color: rgba(255, 255, 255, 0.9);
          margin-bottom: 0.5rem;
        }
        input,
        select,
        textarea {
          padding: 0.75rem 1rem;
          border-radius: 8px;
          border: 1px solid rgba(255, 255, 255, 0.2);
          background: rgba(255, 255, 255, 0.1);
          color: rgba(255, 255, 255, 0.9);
          font-size: 16px;
          outline: none;
          transition: border-color 0.2s ease;

          &:focus {
            border-color: rgba(255, 255, 255, 0.4);
          }
        }

        textarea {
          resize: vertical;
        }
      }

      .form-row {
        display: flex;
        gap: 1rem;

        @include respond-below(sm) {
          gap: 1.5rem;
          flex-direction: column;
        }

        .country-code,
        .mobile-number {
          flex: 1;
        }
      }

      .country-code-select {
        color: rgba(255, 255, 255, 0.9);
        background: rgba(30, 30, 40, 0.8);
        font-size: 15px;
        max-height: 300px;
        overflow-y: auto;

        option {
          padding: 8px;
          background: rgba(30, 30, 40, 1);
          color: rgba(255, 255, 255, 0.9);
        }
      }
      .form-btn-group {
        margin-top: 0.3rem;
        display: flex;
        justify-content: center;
        .submit-button {
          padding: 0.7rem 1.3rem;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1.5rem;
          background: rgb(13, 13, 13);
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.2);
          transition: transform 0.2s ease, box-shadow 0.2s ease;

          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
          }
          @include respond-below(xs) {
            padding: 0.6rem 0.8rem;
            background-color: transparent;
            gap: 0.2rem;
          }
        }
      }
    }
  }
}
</style>
